/* ---------------------------------------------------------------
   Fit finder
   Four questions, no wrong answers:
     1. sizing system  — womens | mens | unisex
     2. garment        — index of a garment block
     3. usual size     — one of the section's sizes
     4. fit preference — boxy | standard | fitted

   The size list is the customer's own scale. Each garment carries its
   own, narrower range — an older design may go down to XXS where a
   newer one starts at S — so the answer is mapped onto the nearest
   size that design is actually made in.

   The recommendation walks the size list from the customer's usual
   size: women's sizing steps down one to reach the unisex baseline,
   then the garment's own cut is weighed against the fit they want.
   A relaxed garment is already loose, so only "more fitted" moves it
   (down one). A regular garment is already close, so only "boxy"
   moves it (up one). Everything is clamped to the available range.
--------------------------------------------------------------- */
(function () {
  'use strict';

  var STEPS = ['system', 'garment', 'size', 'fit'];

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function fill(template, values) {
    if (!template) return '';
    return String(template).replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  // "key | value" or "key | label | description" per line.
  function parseMap(text) {
    var map = {};
    String(text || '')
      .split(/\r?\n/)
      .forEach(function (line) {
        if (!line.trim()) return;
        var parts = line.split('|').map(function (part) {
          return part.trim();
        });
        if (!parts[0]) return;
        map[parts[0]] = parts.slice(1).join(' | ');
      });
    return map;
  }

  function join(parts) {
    return parts
      .filter(function (part) {
        return part && String(part).trim().length;
      })
      .map(function (part) {
        return String(part).trim();
      })
      .join(' ');
  }

  class FitFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.card = this.querySelector('.ff-card');
      this.stepEls = Array.prototype.slice.call(this.querySelectorAll('[data-ff-step]'));
      this.resultEl = this.querySelector('[data-ff-result]');
      this.backWrap = this.querySelector('[data-ff-back]');
      this.backBtn = this.querySelector('[data-ff-back-btn]');
      this.progressEl = this.querySelector('[data-ff-progress]');
      this.progressFill = this.querySelector('[data-ff-progress-fill]');
      this.progressCursor = this.querySelector('[data-ff-progress-cursor]');
      this.progressLabel = this.querySelector('[data-ff-progress-label]');

      if (!this.stepEls.length || !this.resultEl) return;

      this.sizes = (this.getAttribute('data-sizes') || 'S,M,L,XL,XXL,XXXL')
        .split(',')
        .map(function (size) {
          return size.trim().toUpperCase();
        })
        .filter(Boolean);

      try {
        this.copy = JSON.parse(this.getAttribute('data-copy') || '{}');
      } catch (error) {
        this.copy = {};
      }

      this.leads = parseMap(this.copy.leads);
      this.shapes = parseMap(this.copy.shapes);
      this.advice = parseMap(this.copy.advice);

      this.measureLabels = String(this.copy.measureLabels || '')
        .split(',')
        .map(function (label) {
          return label.trim();
        });

      // First line is the title, the rest is the tip — but a single line is
      // treated as the tip itself, so a field saved without a title line
      // still gets its placeholders filled instead of rendering as a heading.
      var tipLines = String(this.copy.proTip || '')
        .split(/\r?\n/)
        .map(function (line) {
          return line.trim();
        })
        .filter(Boolean);

      if (tipLines.length > 1) {
        this.tipTitle = tipLines.shift();
        this.tipBody = tipLines.join(' ');
      } else {
        this.tipTitle = '';
        this.tipBody = tipLines[0] || '';
      }

      this.accents = Array.isArray(this.copy.accents) ? this.copy.accents : [];

      this.answers = { system: null, garment: null, size: null, fit: null };
      this.stepIndex = 0;
      this.advanceTimer = null;

      this.onChoice = this.onChoice.bind(this);
      this.onBack = this.onBack.bind(this);
      this.onRestart = this.onRestart.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.choiceButtons = Array.prototype.slice.call(this.querySelectorAll('[data-ff-choice]'));
      var self = this;
      this.choiceButtons.forEach(function (button) {
        button.addEventListener('click', self.onChoice);
      });
      if (this.backBtn) this.backBtn.addEventListener('click', this.onBack);
      this.resultEl.addEventListener('click', this.onRestart);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.render();
    }

    disconnectedCallback() {
      var self = this;
      window.clearTimeout(this.advanceTimer);
      this.choiceButtons.forEach(function (button) {
        button.removeEventListener('click', self.onChoice);
      });
      if (this.backBtn) this.backBtn.removeEventListener('click', this.onBack);
      if (this.resultEl) this.resultEl.removeEventListener('click', this.onRestart);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.initialised = false;
    }

    /* ---------- flow ---------- */

    onChoice(event) {
      var button = event.currentTarget;
      var key = button.getAttribute('data-ff-choice');
      var value = button.getAttribute('data-value');

      this.answers[key] = value;

      if (key === 'garment') {
        try {
          this.garment = JSON.parse(button.getAttribute('data-garment') || '{}');
        } catch (error) {
          this.garment = null;
        }
        this.applyAccent();
      }

      // Mark selection immediately, then move on so the choice is seen.
      var group = button.closest('.ff-options');
      if (group) {
        Array.prototype.slice.call(group.querySelectorAll('[data-ff-choice]')).forEach(function (other) {
          var active = other === button;
          other.classList.toggle('is-selected', active);
          other.setAttribute('aria-checked', active ? 'true' : 'false');
        });
      }

      var self = this;
      window.clearTimeout(this.advanceTimer);
      this.advanceTimer = window.setTimeout(function () {
        if (self.stepIndex < STEPS.length - 1) {
          self.stepIndex += 1;
        } else {
          self.stepIndex = STEPS.length;
        }
        self.render();
      }, 220);
    }

    // A garment can claim one of the four accents; everything driven by
    // --ff-accent then follows it. Anything else is left alone.
    applyAccent() {
      var choice = this.garment && this.garment.accent;
      var index = parseInt(choice, 10);

      if (!choice || choice === 'inherit' || isNaN(index) || !this.accents[index - 1]) {
        this.style.removeProperty('--ff-accent');
        return;
      }

      this.style.setProperty('--ff-accent', this.accents[index - 1]);
    }

    onBack(event) {
      event.preventDefault();
      if (this.stepIndex === 0) return;
      this.stepIndex -= 1;
      this.render();
    }

    onRestart(event) {
      var trigger = event.target.closest('[data-ff-restart]');
      if (!trigger) return;
      event.preventDefault();
      this.reset();
    }

    reset() {
      this.answers = { system: null, garment: null, size: null, fit: null };
      this.garment = null;
      this.stepIndex = 0;
      this.style.removeProperty('--ff-accent');
      this.choiceButtons.forEach(function (button) {
        button.classList.remove('is-selected');
        button.setAttribute('aria-checked', 'false');
      });
      this.render();
      if (this.card && typeof this.card.scrollIntoView === 'function') {
        this.card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      // Jump to the garment step so the merchant sees the block they picked.
      this.stepIndex = 1;
      this.render();
    }

    /* ---------- rendering ---------- */

    render() {
      var showingResult = this.stepIndex >= STEPS.length;
      var self = this;

      this.stepEls.forEach(function (step, i) {
        var active = !showingResult && i === self.stepIndex;
        step.classList.toggle('is-active', active);
        step.hidden = !active;
      });

      this.resultEl.hidden = !showingResult;
      if (showingResult) {
        this.resultEl.innerHTML = this.buildResult();
      } else {
        this.resultEl.innerHTML = '';
      }

      if (this.backWrap) {
        this.backWrap.hidden = showingResult || this.stepIndex === 0;
      }

      // The cursor sits on the question being answered, so step one reads zero
      // and the far end of the tape is the recommendation.
      var travelled = showingResult ? 100 : (this.stepIndex / STEPS.length) * 100;

      if (this.progressFill) this.progressFill.style.width = travelled + '%';
      if (this.progressCursor) this.progressCursor.style.left = travelled + '%';
      if (this.progressEl) this.progressEl.setAttribute('aria-valuenow', Math.round(travelled));

      if (this.progressLabel) {
        this.progressLabel.textContent = showingResult
          ? (this.copy.rulerEnd || '')
          : this.stepIndex + 1 + ' / ' + STEPS.length;
      }
    }

    /* ---------- recommendation ---------- */

    recommend() {
      // Two scales are at work: the customer's own (every size they might say
      // they wear) and the design's (only what this one is produced in). The
      // maths runs on the customer's scale, then lands on the nearest size the
      // design actually comes in.
      var scale = this.sizes;
      var answers = this.answers;
      var garment = this.garment || {};
      var fitType = garment.fitType === 'regular' ? 'regular' : 'relaxed';

      var wanted = scale.indexOf(String(answers.size || '').toUpperCase());
      if (wanted < 0) wanted = Math.floor(scale.length / 2);

      // Women's sizing runs smaller than unisex, so step down to the baseline.
      if (answers.system === 'womens') wanted -= 1;

      if (fitType === 'relaxed') {
        if (answers.fit === 'fitted') wanted -= 1;
      } else if (answers.fit === 'boxy') {
        wanted += 1;
      }

      if (wanted < 0) wanted = 0;
      if (wanted > scale.length - 1) wanted = scale.length - 1;

      var rows = Array.isArray(garment.measurements) ? garment.measurements : [];
      var offered = rows
        .map(function (row, i) {
          return { row: row, i: i, pos: scale.indexOf(String(row.size).toUpperCase()) };
        })
        .filter(function (entry) {
          return entry.pos >= 0;
        });

      if (!offered.length) {
        return {
          size: scale[wanted],
          note: null,
          measurements: null,
          fitType: fitType,
          garment: garment
        };
      }

      // Nearest size this design is made in, measured on the customer's scale.
      var closest = offered[0];
      offered.forEach(function (entry) {
        if (Math.abs(entry.pos - wanted) < Math.abs(closest.pos - wanted)) closest = entry;
      });

      var smallest = offered[0];
      var largest = offered[offered.length - 1];
      offered.forEach(function (entry) {
        if (entry.pos < smallest.pos) smallest = entry;
        if (entry.pos > largest.pos) largest = entry;
      });

      // The note only earns its place when the design couldn't meet the
      // customer where they were — landing exactly on the smallest size it
      // comes in is a normal result, not an edge case.
      var note = null;
      if (wanted < smallest.pos) note = this.copy.noteSmallest || null;
      if (wanted > largest.pos) note = this.copy.noteLargest || null;

      return {
        size: String(closest.row.size).toUpperCase(),
        note: note,
        measurements: closest.row,
        fitType: fitType,
        garment: garment
      };
    }

    buildExplanation(result) {
      var answers = this.answers;
      var values = {
        size: answers.size || '',
        garment: (result.garment.name || '').toLowerCase(),
        chest: result.measurements ? result.measurements.chest : ''
      };

      var lead = this.leads[answers.system] || '';
      var shape = this.shapes[result.fitType] || '';
      var advice = this.advice[result.fitType + '.' + (answers.fit || 'standard')] || '';

      return join([fill(lead, values), fill(shape, values), fill(advice, values)]);
    }

    buildResult() {
      var copy = this.copy;
      var result = this.recommend();
      var m = result.measurements;
      var html = '';

      html += '<div class="ff-result__inner">';
      html += '<span class="ff-result__rule" aria-hidden="true"></span>';
      html += '<p class="ff-result__eyebrow">' + escapeHtml(copy.recommendedLabel || 'Your recommended size') + '</p>';
      html += '<p class="ff-result__size">' + escapeHtml(result.size) + '</p>';

      var subtitle = join([
        result.garment.name,
        result.garment.fitLabel ? '— ' + result.garment.fitLabel : ''
      ]);
      if (subtitle) {
        html += '<p class="ff-result__garment">' + escapeHtml(subtitle) + '</p>';
      }

      if (m) {
        html += '<div class="ff-measure">';
        html += '<p class="ff-measure__title">' +
          escapeHtml(join([copy.measurementsLabel || 'Measurements for size', result.size])) + '</p>';
        html += '<div class="ff-measure__grid">';
        var self = this;
        [m.chest, m.length, m.sleeve].forEach(function (value, i) {
          html += '<div class="ff-measure__cell"><span class="ff-measure__value">' + escapeHtml(value) +
            '</span><span class="ff-measure__label">' + escapeHtml(self.measureLabels[i] || '') + '</span></div>';
        });
        html += '</div></div>';
      }

      var explanation = this.buildExplanation(result);
      if (explanation) {
        html += '<p class="ff-result__explanation">' + escapeHtml(explanation) + '</p>';
      }

      if (result.note) {
        var noteText = fill(result.note, {
          garment: (result.garment.name || '').toLowerCase(),
          chest: m ? m.chest : '',
          size: this.answers.size || ''
        });
        html += '<p class="ff-result__note">' + escapeHtml(noteText) + '</p>';
      }

      if (this.tipBody || this.tipTitle) {
        var tipValues = {
          garment: (result.garment.name || '').toLowerCase(),
          chest: m ? m.chest : '',
          size: result.size
        };
        html += '<div class="ff-tip">';
        if (this.tipTitle) {
          html += '<p class="ff-tip__title">' + escapeHtml(fill(this.tipTitle, tipValues)) + '</p>';
        }
        if (this.tipBody) {
          html += '<p class="ff-tip__body">' + escapeHtml(fill(this.tipBody, tipValues)) + '</p>';
        }
        html += '</div>';
      }

      if (copy.disclaimer) {
        html += '<p class="ff-result__disclaimer">' + escapeHtml(copy.disclaimer) + '</p>';
      }

      html += '<button type="button" class="ff-link ff-result__restart" data-ff-restart>' +
        '<span aria-hidden="true">&larr;</span> ' + escapeHtml(copy.restart || 'Take new measurements') + '</button>';
      html += '</div>';

      return html;
    }
  }

  if (!customElements.get('fit-finder')) {
    customElements.define('fit-finder', FitFinder);
  }
})();
